import React from 'react';
import { cn } from '@/lib/utils';

interface SwitchProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    size?: 'sm' | 'default' | 'lg';
    label?: string;
    description?: string;
    className?: string;
}

const Switch: React.FC<SwitchProps> = ({
                                           checked,
                                           onCheckedChange,
                                           disabled = false,
                                           size = 'default',
                                           label,
                                           description,
                                           className
                                       }) => {
    const sizeClasses = {
        sm: {
            track: 'h-4 w-7',
            thumb: 'h-3 w-3',
            translate: 'translate-x-3'
        },
        default: {
            track: 'h-5 w-9',
            thumb: 'h-4 w-4',
            translate: 'translate-x-4'
        },
        lg: {
            track: 'h-6 w-11',
            thumb: 'h-5 w-5',
            translate: 'translate-x-5'
        }
    };

    const { track, thumb, translate } = sizeClasses[size];

    return (
        <div className={cn("flex items-center gap-3", className)}>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onCheckedChange(!checked)}
                className={cn(
                    "relative inline-flex flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark-primary",
                    track,
                    checked
                        ? "bg-primary-600"
                        : "bg-gray-200 dark:bg-gray-700",
                    disabled && "opacity-50 cursor-not-allowed"
                )}
            >
                <span
                    className={cn(
                        "pointer-events-none inline-block rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out",
                        thumb,
                        checked ? translate : "translate-x-0"
                    )}
                />
            </button>

            {(label || description) && (
                <div className="flex flex-col">
                    {label && (
                        <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                            {label}
                        </span>
                    )}
                    {description && (
                        <span className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {description}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default Switch;